/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { AxiosError, AxiosResponse } from 'axios'
import { assign, interpret } from 'xstate'
import { createModel } from 'xstate/lib/model'

import { UserType } from 'src/types/customer'
import { Depot } from 'src/types/depot'
import { FustAmount } from 'src/types/fust'
import { FustCard } from 'src/types/fustCard'
import { WorkArea } from 'src/types/workArea'

import {
  batchHandleDirectTransactions,
  divideTransaction,
  lateDivideTransaction,
  queryIssuanceDirectTransactions,
  updateTransactionStatus,
  validateTransaction,
} from '../services/transactionService'
import { FustTransaction, TransactionStatus } from '../types/transaction'
import { ScanFustPassFlowMachineContext } from './scanFustPassFlowMachine'

export type IssuanceDirectTransactionsMachineContext = {
  selectedTransactions: FustTransaction[]
  listOfTransactions: FustTransaction[]
  selectedTransaction?: FustTransaction
  updatedFustAmount?: FustAmount[]
  fustAmount: FustAmount[]
  selectedDepartureLocation?: Depot
  scannedFustPassContext?: ScanFustPassFlowMachineContext
  hasLateDividedTransaction: boolean
  hasEditedTransaction: boolean
  numberOfCustomers: number
  updatedNumberOfCustomers: number
  error?: string
  selectedWorkArea?: WorkArea
  singleCustomerTransactions?: FustTransaction[]
}

const initialState: IssuanceDirectTransactionsMachineContext = {
  listOfTransactions: [],
  selectedTransactions: [],
  fustAmount: [],
  hasLateDividedTransaction: false,
  hasEditedTransaction: false,
  numberOfCustomers: 0,
  updatedNumberOfCustomers: 0,
  error: undefined,
  selectedWorkArea: undefined,
  singleCustomerTransactions: [],
}

export const issuanceDirectTransactionsMachineModel = createModel(initialState, {
  events: {
    LOAD_TRANSACTIONS_LIST: () => ({}),
    HANDLE_SELECTED_TRANSACTIONS: () => ({}),
    ASSIGN_SELECTED_TRANSACTIONS: (transactions: FustTransaction[]) => ({
      transactions,
    }),

    ASSIGN_FUST_AMOUNT: (fustAmount: FustTransaction['fustAantallen']) => ({
      fustAmount,
    }),
    ASSIGN_SELECTED_TRANSACTION: (transaction: FustTransaction) => ({
      transaction,
    }),
    ASSIGN_EDIT_TRANSACTION: (transaction: FustTransaction) => ({
      transaction,
    }),
    UPDATE_SELECTED_TRANSACTION_FUST_AMOUNT: (fustAmount: FustAmount[]) => ({
      fustAmount,
    }),
    UPDATE_TRANSACTION_STATUS: (txId: string, params: FustCard) => ({
      txId,
      params,
    }),
    CLOSE_TRANSACTION_DETAIL_VIEW: () => ({}),
    HANDLE_TRANSACTION: () => ({}),
    HANDLE_DIVIDE_TRANSACTION: (
      txId: FustTransaction['id'],
      fustAmount: FustTransaction['fustAantallen']
    ) => ({
      txId,
      fustAmount,
    }),
    HANDLE_EDIT_TRANSACTION: (
      txId: FustTransaction['id'],
      fustAmount: FustTransaction['fustAantallen']
    ) => ({
      txId,
      fustAmount,
    }),
    ASSIGN_DEPARTURE_LOCATION: (departureLocation: Depot) => ({ departureLocation }),
    HANDLE_DEPARTURE_LOCATION: () => ({}),
    ASSIGN_SCANNED_FUST_PASS_CONTEXT: (
      scanContext: ScanFustPassFlowMachineContext
    ) => ({ scanContext }),
    CLOSE_FUST_PASS_NUMBER_DIALOG: () => ({}),
    CLOSE_SUCCESS_DIALOG: () => ({}),
    RESET_ERROR: () => ({}),
    CONTINUE: () => ({}),
    ASSIGN_SINGLE_CUSTOMER_TRANSACTIONS: () => ({}),
  },
})

export const issuanceDirectTransactionsMachine =
  /** @xstate-layout N4IgpgJg5mDOIC5QEtawK4EMB2BjMAIsgE5i4AuAKsTrJhcgPbawBiANowO4B0AbsjBdk2KAH12qcmMYAzMeRot65JiwDEANQCSAUQDqYygCUAggDkAyqYDClbQHlzYgrsqntAGUSgADo1hkVWYfEAAPRAAWAGZongAOAAZIgHZolJiU+PT4yIAaEABPRBTEgEYeRKrEgCYANjL4+IBWAE5E5oBfToLUDBx8IlIKaloVNTZOXgEhEXFJWGk5BSU6BmZYdQAJCwJPXTFLXX27XQIjMytbeydLUP9A4OxQiIQYuKTU9MzsrPyixA1GqJHitMEZUrZeJlaLNaLdXpoLB4QgkMhUVbjDYcbj8QTCUQSKQyeSKMbrDSeBymc4mCzWOyOKxiTzaSyUe4BIJqF5RWIJZJpDI5HL-YpveqVaqJXJ1BplVJ1BEgPrIwZokaYimTXEzAnzYnLMnKbXqUyWSzaADiziOJ0oZwu9OuTLuSBAD25IXdr3iKQS8RqrWaZQydUikQ6BXFdWSPEiNXidRDdVhrVj8WVqoGqOGGPJTx103xcyJixJKwLEzNFutzlYAFV2WJTABZBwN8wc92ep68hAwirpMqNP3RSKhyJ1aOIEP+hMtEOJOo1MrlSJZpE5obo0Ymws44uzQkLJakrWF9QNgAKBFMDqdV0ZTkO7koTc5jx5Pr5H0F3xFP4ZwQZoI1BMFWhqBM4VabIak3foUR3TUq2xKYeHQXwIEwVRCWNNYnjERYcPQTYIGYMAeBEPhGAAa0o7MkI1fN9wmQ8MKwnDS3wrFsCI8gSNgBBqMYXAuOYABtRIAF1Py9Z4fxAmUeDKZpUyyFJ2naSJ4mAyJ03AsFonidNWhSMooIQtVc13C82PQzDsNw8QeIpfjBPUMBiGIRhiB4Xx2Bw2RfIAWyorcmLzPcCPs3FHK4vC7OYdzyFI4TsBosSnik2Sey5PtFJHONw2XFI4Qs5pNN0gEQKDHhYmiRoJ3HVoylaDcehVCL1SipKWHYvVuL6sQIDAATkHYTYbCpI5HwZG5nFcdwvDEHQDDkgrQFeIrIh4Eq6jKxqakq2DgNKCppXqUc2g6Kzt2Y6LeKLHgAAscAgSREtQjQNu-LbEB2vbIwO8rjqq4C2t2iCsjqP0Iyg2I7si2zvuezhMAgIbUfUcjsAYjK6IY7qbJQ1i0NxdHMa+smWHSzLxOwHLfu9f6QOaZpQQaGVk2XSqp2A2J4kM9oskg464S6TrGJ6lGabRxgMaxuXPO83z-MC8hguIMLpZJliYvJ3hKaVg3aZErK1CZvKvxZ8IAbXXb9sOirwZqicQQa2FmmyHTEliDrEUQmXSdN57SEG6nQ6IuZ2DAbZdn2OaXScZmFNZyc4kgsyg2gr3gLaOp4zBbIRySBpjKR4P9ae9jw5LSOnuj0RY-UAhtB0Vwk+fcxU-7DPQSDFIc+OvOatDQuIPaNoUjSQMA66oO9ce7Va7ACOXL62AxBC9B2FUAK452cw9gOOknwW3vCuFAfs-akf2eA+pM+LxrKsq9Jl0rpfN9X9fKzl7eu994tzbh3U+lx5pMkvuna+Wch53wls0YCJlhZJHSO0GoM8lRS2JshauK90KwDALHCgI0wC+EwMQVKpAJCiQZjWS0NoXC6GvKYYw75jAHCpDYe8UDrbyT7umQuU4-RlVAnKaIrRoh6VgoZQMHRUgWV+F-PBy8DyEOIeiMhFCqHoBoZwC2zB47H0Tq4Vh7CGycJZA4HhF9+GbTtggIEkQOZrmDKGP0tQpw1HzgZfSYJGhymyBI7onVsCMFGvAd0utVE-3Qn-U8FZXKFmgY4kcrQb5lVau1BoSYUjATqH4xMlUh7jlfpLQO1lYmo3YvFZy-9TYpVIqk14ot6rAnHImCMCixRRFkZ7ZcH8xwqIenE3U9cN7fTIeNSaLTECBjiBGSCM9vZ+2iFBM6CphawSqsdeeMTRk1PQm9bAH0TZPTmQgXIQ4DrAgOmucyDRH7NBBP434QYWh3Pgjgxe1S5bsWNg3bUlyy7tL9uI9mkjWgC00vVBqU9shVBcSM3qRzcR12PJMgBgRm5gEuQqMqcKuZqRXNEco0KarexqMLBGrUFSwRRbLUOv8JkNMbjvPeyAD74qnCCVcJlVztXcUgmqx1Xkv3gZ85c3zKn3VRf8jRJDpCjR0dQsAtDDFpw9PlP6aTYIZLlH7UWfts7SMpe0GlI5gQ7MzD8qphyFW4hOWcwkshSLSAoWgIiYktW9l1a8EeHxAwBOTGUWG05zUgggtkaIqZ1mwnhHauVTKa7oQxfqNl2om5QFjmIXA7rGAhS8lEvwOrbavH0jwdmLRWotFTFI9ZelK0LkqsuRFaRwyMpDqm9Fa9WXJImIAzlB880FqLcQEt2qbZaorT4mqjVFmJmOuCSMMoKkL3tfK5l6FMYCCpli02+L9V7VjDkdokjSkQ0DCpOUoFPnGTDF2-B6jcQDo2GILyPliA8tjDwflkFIbCvzlsye3SLIWRlRu5N3aCFOvermt9LAP2q2-fY-1ANyigiaLEdMtRzKgQFtCeMXTVLrJaO7bBsrkYwZfVwfF1VxSNClNKFjVRY2hM6EAA */
  issuanceDirectTransactionsMachineModel.createMachine(
    {
      context: issuanceDirectTransactionsMachineModel.initialContext,
      tsTypes: {} as import('./issuanceDirectTransactionsMachine.typegen').Typegen0,
      schema: {
        services: {} as {
          loadTransactions: { data: AxiosResponse<FustTransaction[]> }
          loadTransactionItem: { data: AxiosResponse<[FustTransaction]> }
          updateTransactionStatus: { data: AxiosResponse<void> }
          handleTransactions: {
            data: void[]
          }
          handleLateDividedTransaction: { data: AxiosResponse<void> }
          divideTransaction: { data: AxiosResponse<void> }
          validateTransaction: { data: AxiosResponse<FustAmount[]> }
        },
      },
      predictableActionArguments: true,
      id: 'issuanceDirectTransactionsFlow',
      initial: 'viewing_list_of_transactions',
      states: {
        viewing_list_of_transactions: {
          on: {
            ASSIGN_SELECTED_TRANSACTION: {
              actions: 'assignSelectedTransaction',
              target: 'viewing_transaction_details',
            },
            HANDLE_SELECTED_TRANSACTIONS: {
              actions: 'assignNumberOfCustomers',
              cond: 'oneOrMoreTransactionsSelected',
              target: 'handling_transactions',
            },
            LOAD_TRANSACTIONS_LIST: {
              target: 'loading_transactions',
            },
            ASSIGN_SELECTED_TRANSACTIONS: {
              actions: 'assignSelectedTransactions',
            },
            ASSIGN_FUST_AMOUNT: {
              actions: 'assignFustAmount',
            },
            UPDATE_TRANSACTION_STATUS: {
              target: 'updating_transaction_status',
            },
          },
        },
        updating_transaction_status: {
          invoke: {
            src: 'updateTransactionStatus',
            onDone: [
              {
                target: 'viewing_list_of_transactions',
              },
            ],
            onError: [
              {
                target: 'viewing_list_of_transactions',
                actions: 'assignError',
              },
            ],
          },
        },
        viewing_transaction_details: {
          on: {
            CLOSE_TRANSACTION_DETAIL_VIEW: {
              actions: 'clearSelectedTransaction',
              target: '#issuanceDirectTransactionsFlow',
            },
          },
        },
        handling_transactions: {
          always: [
            {
              cond: 'multipleTransactionsAreSelected',
              target: 'reviewing_transactions_multiple',
            },
            {
              target: 'reviewing_transactions_single',
            },
          ],
        },
        loading_transactions: {
          invoke: {
            src: 'loadTransactions',
            onDone: [
              {
                actions: 'assignLoadedTransactions',
                target: 'viewing_list_of_transactions',
              },
            ],
            onError: [
              {
                target: 'viewing_list_of_transactions',
                actions: 'assignError',
              },
            ],
          },
          tags: 'loading',
        },
        reviewing_transactions_single: {
          on: {
            HANDLE_TRANSACTION: {
              target: 'select_departure_location',
            },
            ASSIGN_SELECTED_TRANSACTION: {
              actions: 'assignSelectedTransaction',
              target: 'dividing_transaction',
            },
          },
        },
        reviewing_transactions_multiple: {
          on: {
            HANDLE_TRANSACTION: {
              target: 'select_departure_location',
            },
            ASSIGN_SELECTED_TRANSACTION: {
              actions: 'assignSelectedTransaction',
              target: 'dividing_transaction',
            },
          },
        },
        select_departure_location: {
          on: {
            ASSIGN_DEPARTURE_LOCATION: {
              actions: 'assignSelectedDepartureLocation',
            },
            HANDLE_DEPARTURE_LOCATION: {
              cond: 'hasDepartureLocation',
              target: 'handling_fust_pass_scan',
            },
          },
        },
        dividing_transaction: {
          on: {
            UPDATE_SELECTED_TRANSACTION_FUST_AMOUNT: {
              actions: 'updateSelectedTransactionFustAmount',
            },
            HANDLE_DIVIDE_TRANSACTION: [
              {
                cond: 'hasDepartureLocation',
                target: 'late_divide_transaction',
              },
              {
                target: 'divide_transaction',
              },
            ],
          },
        },
        editing_transaction: {
          on: {
            UPDATE_SELECTED_TRANSACTION_FUST_AMOUNT: {
              actions: 'updateSelectedTransactionFustAmount',
            },
            HANDLE_EDIT_TRANSACTION: {
              target: 'edit_transaction',
            },
          },
        },
        late_divide_transaction: {
          invoke: {
            src: 'validateTransaction',
            onDone: [
              {
                actions: [
                  'updateSelectedTransactionsFustAmount',
                  'setHasLateDividedTransaction',
                ],
                target: 'reviewing_transactions_single_customer',
              },
            ],
            onError: { target: 'dividing_transaction', actions: 'assignError' },
          },
          tags: 'loading',
        },
        edit_transaction: {
          invoke: {
            src: 'validateTransaction',
            onDone: [
              {
                actions: [
                  'updateSelectedTransactionsFustAmount',
                  'setHasEditedTransaction',
                ],
                target: 'reviewing_transactions_single_customer',
              },
            ],
            onError: { target: 'editing_transaction', actions: 'assignError' },
          },
          tags: 'loading',
        },
        divide_transaction: {
          invoke: {
            src: 'divideTransaction',
            onDone: [
              {
                actions: [
                  'updateSelectedTransactionsFustAmount',
                  'clearSelectedTransaction',
                ],
                target: 'handling_transactions',
              },
            ],
            onError: { target: 'dividing_transaction', actions: 'assignError' },
          },
          tags: 'loading',
        },
        handling_fust_pass_scan: {
          on: {
            ASSIGN_SCANNED_FUST_PASS_CONTEXT: {
              actions: [
                'assignScannedFustPassContext',
                'assignSingleCustomerTransactions',
              ],
              target: 'reviewing_transactions_single_customer',
            },
            CLOSE_FUST_PASS_NUMBER_DIALOG: {
              target: 'select_departure_location',
            },
          },
        },
        reviewing_transactions_single_customer: {
          on: {
            HANDLE_TRANSACTION: {
              target: 'confirming_with_fust_pass',
            },
            ASSIGN_SELECTED_TRANSACTION: {
              actions: 'assignSelectedTransaction',
              target: 'dividing_transaction',
            },
            ASSIGN_EDIT_TRANSACTION: {
              actions: 'assignSelectedTransaction',
              target: 'editing_transaction',
            },
          },
        },
        confirming_with_fust_pass: {
          on: {
            CONTINUE: {
              target: 'submitting_transactions',
            },
            CLOSE_FUST_PASS_NUMBER_DIALOG: {
              target: 'reviewing_transactions_single_customer',
            },
          },
        },
        submitting_transactions: {
          always: [
            {
              cond: 'hasLateDividedTransactions',
              target: 'handling_late_divided_transactions_submission',
            },
            {
              target: 'handling_transactions_submission',
            },
          ],
        },
        handling_transactions_submission: {
          invoke: {
            src: 'handleTransactions',
            onDone: [
              {
                target: 'showing_success_dialog',
              },
            ],
            onError: [
              {
                target: 'reviewing_transactions_single_customer',
                actions: 'assignError',
              },
            ],
          },
          tags: 'loading',
        },
        handling_late_divided_transactions_submission: {
          invoke: {
            src: 'handleLateDividedTransaction',
            onDone: [
              {
                target: 'showing_success_dialog',
              },
            ],
            onError: [
              {
                target: 'reviewing_transactions_single_customer',
                actions: 'assignError',
              },
            ],
          },
          tags: 'loading',
        },
        showing_success_dialog: {
          on: {
            CLOSE_SUCCESS_DIALOG: [
              {
                cond: 'isLastTransaction',
                target: 'completed_transactions',
                actions: 'updateNumberOfCustomers',
              },
              {
                target: 'handling_fust_pass_scan',
                actions: 'updateNumberOfCustomers',
              },
            ],
          },
        },
        completed_transactions: {
          always: {
            actions: 'resetToInitialState',
            target: 'viewing_list_of_transactions',
          },
        },
      },
      on: {
        RESET_ERROR: { actions: 'resetError' },
      },
      exit: ['resetTransactionStatus'],
    },
    {
      guards: {
        isLastTransaction: context =>
          context.updatedNumberOfCustomers === context.numberOfCustomers,
        hasLateDividedTransactions: context => context.hasLateDividedTransaction,
        multipleTransactionsAreSelected: context =>
          context.selectedTransactions.length > 1,
        oneOrMoreTransactionsSelected: context =>
          context.selectedTransactions.length > 0,
        hasDepartureLocation: context => !!context.selectedDepartureLocation,
      },
      actions: {
        resetTransactionStatus: (context, event) => {
          if (event.type === 'xstate.stop') {
            context.selectedTransactions.forEach(tx =>
              updateTransactionStatus(tx.id, {
                NewStatus: TransactionStatus.Nieuw,
              })
            )
          }
        },
        assignError: assign((_context, event) => ({
          error: String((event.data as AxiosError).response?.data),
        })),
        resetError: assign(_ => ({ error: undefined })),
        updateNumberOfCustomers: assign(context => ({
          updatedNumberOfCustomers: context.updatedNumberOfCustomers + 1,
          hasLateDividedTransaction: false,
          hasEditedTransaction: false,
        })),
        assignNumberOfCustomers: assign(context => ({
          numberOfCustomers: context.selectedTransactions
            .map(transaction => transaction.klantnummer)
            .filter((value, index, self) => self.indexOf(value) === index).length,
          updatedNumberOfCustomers: 1,
        })),
        assignSelectedTransaction: assign((_, event) => ({
          selectedTransaction: event.transaction,
          updatedFustAmount: event.transaction.fustAantallen,
        })),
        updateSelectedTransactionFustAmount: assign((_, event) => {
          return {
            updatedFustAmount: event.fustAmount,
          }
        }),
        updateSelectedTransactionsFustAmount: assign(context => {
          return {
            selectedTransactions: context.selectedTransactions.map(transaction => {
              if (transaction.id === context.selectedTransaction?.id) {
                transaction.fustAantallen = context.updatedFustAmount!
                return transaction
              }
              return transaction
            }),
          }
        }),
        setHasLateDividedTransaction: assign(_ => ({
          hasEditedTransaction: false,
          hasLateDividedTransaction: true,
        })),
        setHasEditedTransaction: assign(_ => ({
          hasEditedTransaction: true,
          hasLateDividedTransaction: false,
        })),
        clearSelectedTransaction: assign(_ => {
          return { selectedTransaction: undefined, updatedFustAmount: undefined }
        }),
        assignSelectedTransactions: assign((_, event) => ({
          selectedTransactions: event.transactions,
        })),
        assignFustAmount: assign((_, event) => ({
          fustAmount: event.fustAmount,
        })),
        assignLoadedTransactions: assign((_context, event) => ({
          listOfTransactions: event.data.data,
        })),
        assignSelectedDepartureLocation: assign((_context, event) => {
          return {
            selectedDepartureLocation: event.departureLocation,
          }
        }),
        assignScannedFustPassContext: assign((context, event) => {
          return {
            scannedFustPassContext: event.scanContext,
            selectedTransaction: context.selectedTransactions.find(
              tx => tx.klantnummer === event.scanContext.scannedFustPass?.klant.nummer
            ),
          }
        }),
        resetToInitialState: assign(_ => {
          return {
            ...initialState,
            selectedDepartureLocation: undefined,
            scannedFustPassContext: undefined,
          }
        }),
        assignSingleCustomerTransactions: assign(context => {
          const singleCustomerTransactions = context.selectedTransactions.filter(
            transaction => {
              if (
                context?.scannedFustPassContext?.scannedFustPass?.klant.type ===
                UserType.Transporter
              ) {
                return (
                  transaction.vervoerderNummer ===
                  context.scannedFustPassContext?.scannedFustPass?.klant.nummer
                )
              }
              return (
                transaction.klantnummer ===
                context.scannedFustPassContext?.scannedFustPass?.klant.nummer
              )
            }
          )
          return {
            singleCustomerTransactions,
          }
        }),
      },
      services: {
        handleTransactions: async context => {
          return batchHandleDirectTransactions(
            context?.singleCustomerTransactions || [],
            String(context?.scannedFustPassContext?.scannedFustPass?.fustpasNummer),
            Number(context?.scannedFustPassContext?.scannedFustPass?.klant?.nummer),
            context.hasEditedTransaction,
            context.selectedDepartureLocation!.code
          )
        },
        handleLateDividedTransaction: async context => {
          return lateDivideTransaction(
            context?.singleCustomerTransactions![0],
            String(context?.scannedFustPassContext?.scannedFustPass?.fustpasNummer),
            context.selectedDepartureLocation!.code
          )
        },
        updateTransactionStatus: async (_context, event) => {
          return updateTransactionStatus(event.txId, event.params)
        },
        loadTransactions: async context =>
          queryIssuanceDirectTransactions(context.selectedWorkArea),
        divideTransaction: async (_context, event) =>
          divideTransaction(event.txId, event.fustAmount),
        validateTransaction: async context =>
          validateTransaction(
            context.selectedTransaction!.id,
            context.selectedTransaction!.klantnummer,
            {
              ...context.selectedTransaction,
              fustAantallen: context.updatedFustAmount!,
            } as FustTransaction
          ),
      },
    }
  )

export const issuanceDirectTransactionsService = interpret(
  issuanceDirectTransactionsMachine
)
